import { SAFE_DPR } from '../constants';
import { normalizeDimensions } from '../shape';
import { getBackNodesHash } from '../utils/layers';
import { resolveRadius } from '../utils/radius';
import { ShaderDisplacementGenerator, fragmentShaders } from './liquidUtils';

// WebGL shader for liquid glass displacement effect
const vertexShaderSource = `
    attribute vec2 a_position;
    attribute vec2 a_texCoord;
    varying vec2 v_texCoord;
    
    void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        v_texCoord = a_texCoord;
    }
`;

const fragmentShaderSource = `
    precision mediump float;
    
    // Inputs
    uniform sampler2D u_texture;
    uniform sampler2D u_displacementMap;
    uniform vec2 u_resolution;

    // Effect controls
    uniform float u_displacementScale;
    uniform float u_aberrationIntensity;
    uniform float u_blur;
    uniform int   u_blurMode;
    uniform float u_scaleFactor;
    uniform float u_saturation;
    uniform float u_noise;
    uniform float u_edgeCurvature;

    // Geometry
    uniform vec2 u_elementSize;
    uniform vec4 u_cornerRadius; // tl, tr, bl, br

    // Lens
    uniform float u_magnification;
    uniform float u_lensStrength;
    uniform vec2  u_lensCenter;
    
    varying vec2 v_texCoord;
    
    #define R u_resolution
    #define PI 3.141592653589793
    #define S smoothstep
    #define PX(a) a/R.y

    mat2 Rot(float a) {
        return mat2(cos(a), sin(-a), sin(a), cos(a)); 
    }

    float Box(vec2 p, vec2 b) {
        vec2 d = abs(p) - b;
        return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
    }

    vec4 LiquidGlass(sampler2D tex, vec2 uv, float direction, float quality, float size) {
        vec2 radius = size / R;
        vec4 color = texture2D(tex, uv);
        
        for (float d = 0.0; d < PI; d += PI / direction) {
            for (float i = 1.0 / quality; i <= 1.0; i += 1.0 / quality) {
                color += texture2D(tex, uv + vec2(cos(d), sin(d)) * radius * i);
            }
        }
        
        color /= quality * direction;
        return color;
    }

    vec4 Icon(vec2 uv) {
        float box = Box(uv, vec2(PX(50.0)));
        float boxShape = S(PX(1.5), 0.0, box - PX(50.0));
        float boxDisp = S(PX(35.0), 0.0, box - PX(25.0));
        float boxLight = boxShape * S(0.0, PX(30.0), box - PX(40.0));
        return vec4(boxShape, boxDisp, boxLight, 0.0);
    }

    // Rounded-rect SDF for masking to element shape
    float roundedRectDistance(vec2 p, vec2 size, vec4 radius) {
        vec2 q = abs(p) - size + vec2(radius.x);
        vec2 r = vec2(radius.x);
        if (p.x > 0.0 && p.y > 0.0) r.x = radius.y; // top-right
        else if (p.x < 0.0 && p.y > 0.0) r.x = radius.x; // top-left
        else if (p.x < 0.0 && p.y < 0.0) r.x = radius.z; // bottom-left
        else if (p.x > 0.0 && p.y < 0.0) r.x = radius.w; // bottom-right
        return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r.x;
    }
    
    float calculateCornerMask(vec2 uv) {
        vec2 pos = (uv - 0.5) * u_elementSize;
        vec2 size = u_elementSize * 0.5;
        float dist = roundedRectDistance(pos, size, u_cornerRadius);
        float edgeSmooth = 1.0 / max(1.0, min(u_elementSize.x, u_elementSize.y)) * 2.0;
        return 1.0 - smoothstep(-edgeSmooth, edgeSmooth, dist);
    }

    // Simple saturation adjust
    vec3 applySaturation(vec3 color, float saturation) {
        float l = dot(color, vec3(0.299, 0.587, 0.114));
        return mix(vec3(l), color, saturation);
    }
    
    void main() {
        vec2 uv = v_texCoord;
        vec2 I = uv * R;
        vec2 st = (I - 0.5 * R) / R.y;
        vec2 M = ((u_lensCenter * R) - 0.5 * R) / R.y;
        
        // Corner mask to keep shape fidelity
        float cornerMask = calculateCornerMask(uv);
        if (cornerMask < 0.001) {
            gl_FragColor = vec4(0.0);
            return;
        }
        
        vec4 icon = Icon(st - M);
        
        // Lens effect with magnification control
        vec2 uv2 = uv - u_lensCenter;
        float lensEffect = 0.5 + 0.5 * S(0.5, 1.0, icon.y);
        
        // Apply magnification based on lens strength and magnification uniform
        float magnificationFactor = mix(1.0, u_magnification, u_lensStrength);
        uv2 *= mix(lensEffect, 1.0 / magnificationFactor, u_lensStrength);
        uv2 += u_lensCenter;
        
        // Clamp to prevent sampling outside texture bounds
        uv2 = clamp(uv2, vec2(0.001), vec2(0.999));
        
        // Map uniforms to liquid glass parameters
        float direction = max(4.0, u_aberrationIntensity * 2.0 + 6.0);
        float quality = max(4.0, u_blur * 2.0 + 6.0);
        float size = max(10.0, u_displacementScale * 0.5);
        
        // Apply liquid glass effect
        vec3 baseColor = texture2D(u_texture, uv).rgb * 0.8;
        vec3 glassColor = 0.2 + LiquidGlass(u_texture, uv2, direction, quality, size).rgb * 0.7;
        vec3 col = mix(baseColor, glassColor, icon.x);
        
        // Add lighting and edge curvature
        col += icon.z * (0.9 * u_edgeCurvature);
        
        // Vignette effect
        float vignette = 1.0 - 0.2 * S(PX(80.0), 0.0, Box(st - M + vec2(0.0, PX(40.0)), vec2(PX(50.0))));
        col *= vignette;
        
        // Apply saturation and noise
        col = applySaturation(col, u_saturation);
        
        // Subtle noise effect
        vec2 noiseUV = uv + texture2D(u_displacementMap, uv * 4.0).rg * 0.01 * u_noise;
        vec3 noiseColor = texture2D(u_texture, noiseUV).rgb;
        col = mix(col, noiseColor, u_noise * 0.05);
        
        gl_FragColor = vec4(col, cornerMask);
    }
`;

// WebGL context and shader management
class GlassRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (!this.gl) {
      console.warn('WebGL not supported, falling back to canvas filters');
      return null;
    }

    this.program = null;
    this.uniforms = {};
    this.attributes = {};
    this.buffers = {};
    this.textures = {};
    this.displacementTexture = null;

    this.init();
  }

  init() {
    const gl = this.gl;

    // Create shaders
    const vertexShader = this.createShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = this.createShader(gl.FRAGMENT_SHADER, fragmentShaderSource);

    // Create program
    this.program = gl.createProgram();
    gl.attachShader(this.program, vertexShader);
    gl.attachShader(this.program, fragmentShader);
    gl.linkProgram(this.program);

    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
      console.error('Program link error:', gl.getProgramInfoLog(this.program));
      return;
    }

    // Get attribute and uniform locations
    this.attributes.position = gl.getAttribLocation(this.program, 'a_position');
    this.attributes.texCoord = gl.getAttribLocation(this.program, 'a_texCoord');

    // Core uniforms
    this.uniforms.texture = gl.getUniformLocation(this.program, 'u_texture');
    this.uniforms.displacementMap = gl.getUniformLocation(this.program, 'u_displacementMap');
    this.uniforms.resolution = gl.getUniformLocation(this.program, 'u_resolution');

    // Effect control uniforms
    this.uniforms.displacementScale = gl.getUniformLocation(this.program, 'u_displacementScale');
    this.uniforms.aberrationIntensity = gl.getUniformLocation(
      this.program,
      'u_aberrationIntensity',
    );
    this.uniforms.blur = gl.getUniformLocation(this.program, 'u_blur');
    this.uniforms.blurMode = gl.getUniformLocation(this.program, 'u_blurMode');
    this.uniforms.scaleFactor = gl.getUniformLocation(this.program, 'u_scaleFactor');
    this.uniforms.saturation = gl.getUniformLocation(this.program, 'u_saturation');
    this.uniforms.edgeCurvature = gl.getUniformLocation(this.program, 'u_edgeCurvature');
    this.uniforms.noise = gl.getUniformLocation(this.program, 'u_noise');

    // 新增透镜效果uniforms
    this.uniforms.magnification = gl.getUniformLocation(this.program, 'u_magnification');
    this.uniforms.lensStrength = gl.getUniformLocation(this.program, 'u_lensStrength');
    this.uniforms.lensCenter = gl.getUniformLocation(this.program, 'u_lensCenter');

    // Element geometry uniforms
    this.uniforms.elementSize = gl.getUniformLocation(this.program, 'u_elementSize');
    this.uniforms.cornerRadius = gl.getUniformLocation(this.program, 'u_cornerRadius');

    // Create buffers
    this.createBuffers();

    // Create displacement texture
    this.createDisplacementTexture();
  }

  createShader(type, source) {
    const gl = this.gl;
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('Shader compile error:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }

    return shader;
  }

  createBuffers() {
    const gl = this.gl;

    // Position buffer (full screen quad)
    const positions = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);

    this.buffers.position = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.position);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    // Texture coordinate buffer
    const texCoords = new Float32Array([0, 1, 1, 1, 0, 0, 1, 0]);

    this.buffers.texCoord = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.texCoord);
    gl.bufferData(gl.ARRAY_BUFFER, texCoords, gl.STATIC_DRAW);
  }

  createDisplacementTexture() {
    if (!this.gl) return;

    // Generate shader-based displacement map
    const generator = new ShaderDisplacementGenerator({
      width: this.canvas.width,
      height: this.canvas.height,
      fragment: fragmentShaders.liquidGlass,
    });

    const displacementCanvas = document.createElement('canvas');
    displacementCanvas.width = this.canvas.width;
    displacementCanvas.height = this.canvas.height;
    const ctx = displacementCanvas.getContext('2d');

    // Generate displacement map
    const dataUrl = generator.updateShader();
    generator.destroy();

    // Load displacement map as image
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0);

      // Create WebGL texture from displacement map
      const gl = this.gl;
      this.displacementTexture = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, this.displacementTexture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, displacementCanvas);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    };
    img.src = dataUrl;
  }

  createTextureFromCanvas(sourceCanvas) {
    const gl = this.gl;
    const texture = gl.createTexture();

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sourceCanvas);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    return texture;
  }

  render(sourceCanvas, options = {}) {
    if (!this.gl || !this.program || !this.displacementTexture) return;

    const gl = this.gl;
    const {
      displacementScale = 1.0,
      aberrationIntensity = 1.0,
      blur = 1.0,
      blurMode = 0,
      scaleFactor = 1.0,
      saturation = 1.0,
      edgeCurvature = 1.0,
      elementSize = { width: 100, height: 100 },
      cornerRadius = { tl: 0, tr: 0, bl: 0, br: 0 },
      // 新增透镜效果参数
      magnification = 1.0,
      lensStrength = 1.0,
      lensCenter = { x: 0.5, y: 0.5 },
      // 新增噪声参数
      noise = 1.0,
    } = options;

    // Set viewport
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Use shader program
    gl.useProgram(this.program);

    // Create texture from source canvas
    const texture = this.createTextureFromCanvas(sourceCanvas);

    // Bind main texture
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.uniform1i(this.uniforms.texture, 0);

    // Bind displacement texture
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.displacementTexture);
    gl.uniform1i(this.uniforms.displacementMap, 1);

    // Set core uniforms
    gl.uniform2f(this.uniforms.resolution, this.canvas.width, this.canvas.height);

    // Set effect control uniforms
    gl.uniform1f(this.uniforms.displacementScale, displacementScale);
    gl.uniform1f(this.uniforms.aberrationIntensity, aberrationIntensity);
    gl.uniform1f(this.uniforms.blur, blur);
    gl.uniform1i(this.uniforms.blurMode, blurMode);
    gl.uniform1f(this.uniforms.scaleFactor, scaleFactor);
    gl.uniform1f(this.uniforms.saturation, saturation);
    gl.uniform1f(this.uniforms.edgeCurvature, edgeCurvature);
    if (this.uniforms.noise) gl.uniform1f(this.uniforms.noise, noise);

    // Set lens effect uniforms
    gl.uniform1f(this.uniforms.magnification, magnification);
    gl.uniform1f(this.uniforms.lensStrength, lensStrength);
    gl.uniform2f(this.uniforms.lensCenter, lensCenter.x, lensCenter.y);

    // Set element geometry uniforms
    gl.uniform2f(this.uniforms.elementSize, elementSize.width, elementSize.height);
    gl.uniform4f(
      this.uniforms.cornerRadius,
      cornerRadius.tl,
      cornerRadius.tr,
      cornerRadius.bl,
      cornerRadius.br,
    );

    // Set up attributes
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.position);
    gl.enableVertexAttribArray(this.attributes.position);
    gl.vertexAttribPointer(this.attributes.position, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.texCoord);
    gl.enableVertexAttribArray(this.attributes.texCoord);
    gl.vertexAttribPointer(this.attributes.texCoord, 2, gl.FLOAT, false, 0, 0);

    // Draw
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    // Cleanup
    gl.deleteTexture(texture);
  }

  destroy() {
    if (!this.gl) return;

    const gl = this.gl;

    if (this.program) {
      gl.deleteProgram(this.program);
    }

    if (this.displacementTexture) {
      gl.deleteTexture(this.displacementTexture);
    }

    Object.values(this.buffers).forEach((buffer) => {
      if (buffer) gl.deleteBuffer(buffer);
    });

    Object.values(this.textures).forEach((texture) => {
      if (texture) gl.deleteTexture(texture);
    });
  }
}

// Get or create a canvas for a specific node
function getNodeCanvas(backdropCache, nodeId, width, height, type = 'temp') {
  if (!backdropCache[nodeId]) {
    backdropCache[nodeId] = {};
  }

  let canvas = backdropCache[nodeId][type];
  if (!canvas) {
    canvas = document.createElement('canvas');
    backdropCache[nodeId][type] = canvas;
  }

  // Only resize if dimensions changed to avoid unnecessary operations
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }

  return canvas;
}

export function drawGlassEffect(renderContext, node) {
  const render = () => {
    // This will be called after the effect is applied
  };

  if (!node.backdropFilter || !node.backdropFilter.includes('glass(')) {
    return render();
  }

  drawLiquidGlassEffectImpl(renderContext, node);
  render();
}

function drawLiquidGlassEffectImpl(renderContext, node) {
  const { context } = renderContext;
  const { scale: scaleProvider } = renderContext.providers;
  const scaleValue = scaleProvider.scale;
  const backdropCache = renderContext.cache.backdropCache || {};
  renderContext.cache.backdropCache = backdropCache;

  const box = renderContext.api.getNodeBox(node);
  const x = node.w < 0 ? node.x + node.w : node.x;
  const y = node.h < 0 ? node.y + node.h : node.y;
  const w = Math.abs(node.w);
  const h = Math.abs(node.h);

  // Early exit if dimensions are too small
  if (w < 1 || h < 1) {
    return;
  }

  context.save();

  try {
    // 使用原始元素尺寸作为基准，不受缩放影响
    const originalElementWidth = w;
    const originalElementHeight = h;

    let canvasWidth = Math.ceil(w * scaleValue);
    let canvasHeight = Math.ceil(h * scaleValue);

    let sourceX = Math.floor(box.x * scaleValue + (scaleProvider.translateX || 0));
    let sourceY = Math.floor(box.y * scaleValue + (scaleProvider.translateY || 0));

    // Store original values for calculating adjustments
    const originalSourceX = sourceX;
    const originalSourceY = sourceY;
    const originalCanvasWidth = canvasWidth;
    const originalCanvasHeight = canvasHeight;

    // Get canvas bounds
    const maxCanvasWidth = context.canvas.width;
    const maxCanvasHeight = context.canvas.height;

    // Calculate the intersection of the node area with the canvas bounds
    const intersectionLeft = Math.max(0, sourceX);
    const intersectionTop = Math.max(0, sourceY);
    const intersectionRight = Math.min(maxCanvasWidth, sourceX + canvasWidth);
    const intersectionBottom = Math.min(maxCanvasHeight, sourceY + canvasHeight);

    // Calculate the actual area we can capture from the canvas
    const captureWidth = Math.max(0, intersectionRight - intersectionLeft);
    const captureHeight = Math.max(0, intersectionBottom - intersectionTop);

    // Early exit if there's no intersection with the canvas
    if (captureWidth <= 0 || captureHeight <= 0) {
      return;
    }

    // Update coordinates and dimensions based on intersection
    sourceX = intersectionLeft;
    sourceY = intersectionTop;
    canvasWidth = captureWidth;
    canvasHeight = captureHeight;

    const zoom = scaleProvider.scale * SAFE_DPR;

    const isScaling = scaleProvider.scaling;

    // Create cache key for the liquid glass effect result
    const cacheKey = getBackNodesHash(renderContext, node);

    // Check if we have a cached result for this exact configuration
    if (!backdropCache.liquidGlassResultCache) {
      backdropCache.liquidGlassResultCache = {};
    }

    const exportMode = renderContext.mode === 'export';

    let filteredCanvas;
    const cacheItem = backdropCache.liquidGlassResultCache[node.id];
    const isZoomMatch = cacheItem && Math.abs(cacheItem.zoom - zoom) < 1;
    if (!exportMode && cacheItem && cacheItem.key === cacheKey && (isZoomMatch || isScaling)) {
      filteredCanvas = cacheItem.canvas;
    } else {
      // Clean up old cache item
      if (cacheItem) {
        if (cacheItem.canvas) {
          cacheItem.canvas.width = 0;
          cacheItem.canvas.height = 0;
        }
      }

      // Only apply filter if the node is at least partially visible on the canvas
      if (
        !(
          sourceX + canvasWidth <= 0 ||
          sourceX >= context.canvas.width ||
          sourceY + canvasHeight <= 0 ||
          sourceY >= context.canvas.height
        )
      ) {
        // Create or get WebGL renderer for this node
        if (!backdropCache[node.id]) {
          backdropCache[node.id] = {};
        }

        if (!backdropCache[node.id].GlassRenderer) {
          const glCanvas = document.createElement('canvas');
          glCanvas.width = canvasWidth;
          glCanvas.height = canvasHeight;
          backdropCache[node.id].GlassRenderer = new GlassRenderer(glCanvas);
        }

        const renderer = backdropCache[node.id].GlassRenderer;

        if (renderer && renderer.gl) {
          // Resize WebGL canvas if needed
          if (renderer.canvas.width !== canvasWidth || renderer.canvas.height !== canvasHeight) {
            renderer.canvas.width = canvasWidth;
            renderer.canvas.height = canvasHeight;
            // Recreate displacement texture with new dimensions
            renderer.createDisplacementTexture();
          }

          // Get background area from the main canvas
          const imageData = context.getImageData(sourceX, sourceY, canvasWidth, canvasHeight);

          // Use node-level cached canvases to reduce allocation overhead
          const tempCanvas = getNodeCanvas(
            backdropCache,
            node.id,
            canvasWidth,
            canvasHeight,
            'temp',
          );
          const tempContext = tempCanvas.getContext('2d');

          // Clear and set the image data
          tempContext.clearRect(0, 0, canvasWidth, canvasHeight);
          tempContext.putImageData(imageData, 0, 0);

          // Parse liquid glass parameters from backdropFilter
          const filterParams = parseLiquidGlassFilter(node.backdropFilter);
          const radius = resolveRadius(node.w, node.h, node.radius);

          // Render liquid glass effect with scale-independent parameters
          renderer.render(tempCanvas, {
            displacementScale: filterParams.displacementScale,
            aberrationIntensity: filterParams.aberrationIntensity,
            blur: filterParams.blur * scaleValue,
            noise: filterParams.noise,
            scaleFactor: scaleValue,
            saturation: filterParams.saturation,
            edgeCurvature: filterParams.edgeCurvature,
            cornerRadius: {
              tl: radius.topLeft,
              tr: radius.topRight,
              bl: radius.bottomLeft,
              br: radius.bottomRight,
            },
            elementSize: {
              width: originalElementWidth,
              height: originalElementHeight,
            },
            // 添加透镜效果参数
            magnification: filterParams.magnification,
            lensStrength: filterParams.lensStrength,
            lensCenter: filterParams.lensCenter,
          });

          // Create a cached result canvas
          filteredCanvas = document.createElement('canvas');
          filteredCanvas.width = canvasWidth;
          filteredCanvas.height = canvasHeight;
          const filteredContext = filteredCanvas.getContext('2d');
          filteredContext.drawImage(renderer.canvas, 0, 0);

          // Cache the filtered result
          backdropCache.liquidGlassResultCache[node.id] = {
            zoom: zoom,
            key: cacheKey,
            canvas: filteredCanvas,
          };
        }
      }
    }

    if (filteredCanvas) {
      // Calculate adjusted logical coordinates and dimensions
      const sourceXOffset = sourceX - originalSourceX;
      const sourceYOffset = sourceY - originalSourceY;
      const widthDiff = originalCanvasWidth - canvasWidth;
      const heightDiff = originalCanvasHeight - canvasHeight;

      const adjustedX = x + sourceXOffset / scaleValue;
      const adjustedY = y + sourceYOffset / scaleValue;
      const adjustedW = w - widthDiff / scaleValue;
      const adjustedH = h - heightDiff / scaleValue;

      // Apply clipping for rounded corners
      clipRoundedRect(context, node);

      // Draw the cached filtered result back onto the main canvas
      context.drawImage(filteredCanvas, x, y, w, h);

      // Debug visualization
      if (renderContext.debug) {
        context.strokeStyle = 'rgba(0, 255, 255, 0.8)';
        context.lineWidth = 2;
        context.strokeRect(adjustedX + 1, adjustedY + 1, adjustedW - 2, adjustedH - 2);
      }
    }
  } catch (error) {
    // getImageData can throw security errors for tainted canvases or other issues.
    // Silently fail to avoid breaking the render pipeline
    console.warn('Liquid glass effect error:', error);
  } finally {
    context.restore();
  }
}

export function parseLiquidGlassFilter(filterString) {
  const defaults = {
    displacementScale: 70,
    aberrationIntensity: 2,
    blur: 5,
    noise: 1.0,
    saturation: 1.0,
    edgeCurvature: 1.0,
    // 新增透镜效果默认参数
    magnification: 1.0, // 1.0 = 无放大，> 1.0 = 放大镜，< 1.0 = 缩小镜
    lensStrength: 1.0, // 透镜强度，控制扭曲程度
    lensCenter: { x: 0.5, y: 0.5 }, // 透镜中心点，相对坐标
  };

  if (!filterString.includes('glass')) {
    return defaults;
  }

  // Parse parameters from filter string like "glass(displacement: 70, aberration: 3, blur: 5, noise: 1.0, saturation: 1.2, curvature: 1.5, magnification: 1.5, strength: 0.8, centerX: 0.5, centerY: 0.5)"
  const paramMatch = filterString.match(/glass\(([^)]+)\)/);
  if (!paramMatch) return defaults;

  const params = paramMatch[1].split(',').reduce((acc, param) => {
    const [key, value] = param.split(':').map((s) => s.trim());
    if (key && value) {
      acc[key] = isNaN(parseFloat(value)) ? defaults[key] : parseFloat(value);
    }
    return acc;
  }, {});

  const result = {
    displacementScale: params.displacement ?? defaults.displacementScale,
    aberrationIntensity: params.aberration ?? defaults.aberrationIntensity,
    blur: params.blur ?? defaults.blur,
    noise: params.noise ?? defaults.noise,
    saturation: params.saturation ?? defaults.saturation,
    edgeCurvature: params.curvature ?? defaults.edgeCurvature,
    // 解析透镜效果参数
    magnification: params.magnification ?? defaults.magnification,
    lensStrength: params.strength ?? defaults.lensStrength,
    lensCenter: {
      x: params.centerX ?? defaults.lensCenter.x,
      y: params.centerY ?? defaults.lensCenter.y,
    },
  };

  return result;
}

function clipRoundedRect(ctx, node) {
  const radius = node.radius;
  if (!radius || radius === 'none') return;
  const { x: normX, y: normY, w: normW, h: normH } = normalizeDimensions(node);
  const { topLeft, topRight, bottomRight, bottomLeft } = resolveRadius(normW, normH, radius);
  ctx.beginPath();
  ctx.moveTo(normX + topLeft, normY);
  ctx.lineTo(normX + normW - topRight, normY);
  ctx.arcTo(normX + normW, normY, normX + normW, normY + topRight, topRight);
  ctx.lineTo(normX + normW, normY + normH - bottomRight);
  ctx.arcTo(normX + normW, normY + normH, normX + normW - bottomRight, normY + normH, bottomRight);
  ctx.lineTo(normX + bottomLeft, normY + normH);
  ctx.arcTo(normX, normY + normH, normX, normY + normH - bottomLeft, bottomLeft);
  ctx.lineTo(normX, normY + topLeft);
  ctx.arcTo(normX, normY, normX + topLeft, normY, topLeft);
  ctx.closePath();
  ctx.clip();
}
